import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Tracks the pointer that started an interaction until it ends anywhere in the document.
 * @param {PointerEvent} startEvent - The pointerdown event
 * @param {() => void} callback - Called when the same pointer ends or is canceled
 * @returns {AbortController} Controller that removes the temporary listeners
 */
function listenForPointerEnd(startEvent, callback) {
  const abortController = new AbortController();

  /** @param {PointerEvent} event */
  const finish = (event) => {
    if (event.pointerId !== startEvent.pointerId) return;
    abortController.abort();
    callback();
  };

  const options = { capture: true, passive: true, signal: abortController.signal };
  document.addEventListener('pointerup', finish, options);
  document.addEventListener('pointercancel', finish, options);

  return abortController;
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  #renderGeneration = 0;

  #renderedURL = '';

  connectedCallback() {
    super.connectedCallback();
    this.#renderedURL = window.location.href;
    window.addEventListener('popstate', this.#handlePopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#handlePopState);
  }

  /**
   * Only one of the responsive facet forms for a section should react to
   * history navigation. It synchronizes every sibling form before rendering.
   * @returns {boolean}
   */
  #isPrimaryForSection() {
    return (
      Array.from(document.querySelectorAll('facets-form-component[section-id]')).find(
        (facetsForm) =>
          facetsForm instanceof FacetsFormComponent && facetsForm.sectionId === this.sectionId
      ) === this
    );
  }

  /** @param {string} url */
  #setRenderedURL(url) {
    for (const facetsForm of document.querySelectorAll('facets-form-component[section-id]')) {
      if (!(facetsForm instanceof FacetsFormComponent) || facetsForm.sectionId !== this.sectionId) continue;
      facetsForm.#renderedURL = url;
    }
  }

  /**
   * Keep URL, controls, result count and product grid in the same state when
   * users navigate through filter/sort history with Back or Forward.
   */
  #handlePopState = () => {
    if (!this.#isPrimaryForSection()) return;

    const urlParameters = new URL(window.location.href).searchParams;
    this.#syncFilterInputs(urlParameters);
    this.dispatchEvent(new FilterUpdateEvent(urlParameters));
    this.#updateSection();
  };

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    // The responsive sorting component keeps a select and radio group in the
    // same form. Only the control for the active viewport is authoritative;
    // normalize it to one URL value so filtering cannot duplicate sort_by.
    const sortControl =
      window.innerWidth < 750
        ? this.refs.facetsForm.querySelector('select[name="sort_by"]:not([disabled])')
        : this.refs.facetsForm.querySelector('input[name="sort_by"]:checked:not([disabled])');
    const sortBy = sortControl instanceof HTMLInputElement || sortControl instanceof HTMLSelectElement
      ? sortControl.value
      : newParameters.get('sort_by');
    newParameters.delete('sort_by');
    if (sortBy) newParameters.set('sort_by', sortBy);

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section.
   *
   * This must run immediately so a new interaction invalidates any older
   * section response before it can overwrite the current form state.
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   * @returns {Promise<string>} The rendered section HTML
   */
  async #updateSection() {
    const generation = ++this.#renderGeneration;
    const requestedURL = window.location.href;

    try {
      const sectionHTML = await sectionRenderer.renderSection(this.sectionId, {
        onBeforeMorph: () => {
          if (generation === this.#renderGeneration) return this.#captureScrollAnchor();
        },
        onAfterMorph: (scrollAnchor) => {
          if (generation !== this.#renderGeneration) return;
          this.#setRenderedURL(requestedURL);
          this.#restoreScrollAnchor(scrollAnchor);
        },
      });

      return sectionHTML;
    } catch (error) {
      if (generation !== this.#renderGeneration) return '';

      // Keep the existing DOM and URL consistent. Re-publish the restored
      // controls so listeners undo their optimistic state, then explicitly
      // release collection loading UI without starting another request.
      const fallbackURL = new URL(this.#renderedURL || window.location.href);
      history.replaceState(history.state, '', fallbackURL.toString());
      this.#syncFilterInputs(fallbackURL.searchParams);
      this.dispatchEvent(new FilterUpdateEvent(fallbackURL.searchParams));
      this.dispatchEvent(new Event(ThemeEvents.FilterUpdateError, { bubbles: true }));
      console.error('Unable to update collection filters.', error);
      return '';
    }
  }

  /**
   * Invalidates the current response before a pointer interaction can finish.
   */
  cancelPendingRender() {
    this.#renderGeneration += 1;
    return sectionRenderer.cancelPendingRender(this.sectionId);
  }

  /**
   * Reattaches rendering after a pointer interaction ends without a change.
   * @returns {Promise<string>} The rendered section HTML
   */
  refreshCurrentSection() {
    return this.#updateSection();
  }

  /**
   * Captures the first facet group's viewport position and scroll container.
   * Keeping that group anchored prevents active-filter pills from moving the
   * remaining controls under the user's pointer when the response is applied.
   */
  #captureScrollAnchor() {
    const facetInput = this.refs.facetsForm.querySelector('facet-inputs-component, price-facet-component');
    const anchor = facetInput?.closest('.facets__item');
    if (!(facetInput instanceof HTMLElement) || !(anchor instanceof HTMLElement) || !facetInput.id) return;

    return {
      id: facetInput.id,
      top: anchor.getBoundingClientRect().top,
      scrollContainer: this.#findScrollContainer(anchor),
    };
  }

  /**
   * @param {{ id: string, top: number, scrollContainer: Element } | undefined} scrollAnchor
   */
  #restoreScrollAnchor(scrollAnchor) {
    if (
      !scrollAnchor ||
      !this.isConnected ||
      !this.refs.facetsForm.isConnected ||
      !scrollAnchor.scrollContainer.isConnected
    ) {
      return;
    }

    const facetInput = Array.from(
      this.refs.facetsForm.querySelectorAll('facet-inputs-component, price-facet-component')
    ).find((element) => element.id === scrollAnchor.id);
    const anchor = facetInput?.closest('.facets__item');
    if (!(facetInput instanceof HTMLElement) || !facetInput.isConnected) return;
    if (!(anchor instanceof HTMLElement) || !anchor.isConnected) return;

    const offset = anchor.getBoundingClientRect().top - scrollAnchor.top;
    if (Math.abs(offset) < 1) return;

    scrollAnchor.scrollContainer.scrollTop += offset;
  }

  /**
   * @param {HTMLElement} element
   * @returns {Element}
   */
  #findScrollContainer(element) {
    let parent = element.parentElement;

    while (parent && parent !== document.body) {
      const overflowY = getComputedStyle(parent).overflowY;
      if (/(auto|scroll)/.test(overflowY) && parent.scrollHeight > parent.clientHeight) return parent;
      parent = parent.parentElement;
    }

    return document.scrollingElement ?? document.documentElement;
  }

  /**
   * Synchronizes filter and sort controls with a URL-driven update before the response arrives.
   * @param {URLSearchParams} urlParameters - The canonical filter parameters
   */
  #syncFilterInputs(urlParameters) {
    // Desktop filters and the mobile drawer coexist in the same section. The
    // morph keeps live input properties, so both forms must be updated first.
    for (const facetsForm of document.querySelectorAll('facets-form-component[section-id]')) {
      if (!(facetsForm instanceof FacetsFormComponent) || facetsForm.sectionId !== this.sectionId) continue;

      for (const element of facetsForm.refs.facetsForm.elements) {
        if (!(element instanceof HTMLInputElement) && !(element instanceof HTMLSelectElement)) continue;

        if (element.name === 'sort_by') {
          const sortBy = urlParameters.get('sort_by');
          if (element instanceof HTMLSelectElement) {
            const defaultOption = Array.from(element.options).find((option) => option.defaultSelected);
            element.value = sortBy ?? defaultOption?.value ?? '';
          } else if (element.type === 'radio') {
            element.checked = sortBy ? element.value === sortBy : element.defaultChecked;
          }
          continue;
        }

        if (!element.name.startsWith('filter.')) continue;

        const values = urlParameters.getAll(element.name);
        if (element.type === 'checkbox' || element.type === 'radio') {
          element.checked = values.includes(element.value);
        } else {
          element.value = values[0] ?? '';
        }
      }
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    const targetURL = new URL(url, window.location.href);
    this.#syncFilterInputs(targetURL.searchParams);
    history.pushState('', '', targetURL.toString());
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  #pointerInteraction;
  #pointerEndController;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopTrackingPointer();
  }

  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    this.#stopTrackingPointer();
    this.#pointerInteraction = undefined;
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Invalidates an older response as soon as an option interaction starts.
   */
  cancelPendingRender() {
    const facetsForm = this.closest('facets-form-component');
    return facetsForm instanceof FacetsFormComponent ? facetsForm.cancelPendingRender() : false;
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Prefetches the section for a facet's predicted post-toggle state.
   * Must be called before the input is toggled.
   * @param {HTMLElement} optionElement - The facet option element
   */
  #prefetchOption(optionElement) {
    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = optionElement.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }

  /**
   * Starts prefetching immediately on pointer down, including on touch devices.
   * @param {PointerEvent} event - The pointer event
   */
  prefetchPageImmediate(event) {
    if (!event.isPrimary || event.button !== 0) return;
    if (!(event.target instanceof HTMLElement)) return;

    const inputElement = event.target.querySelector('input');
    if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled) return;

    this.#stopTrackingPointer();
    const didCancel = this.cancelPendingRender();
    this.#pointerInteraction = didCancel ? { input: inputElement, checked: inputElement.checked } : undefined;
    if (didCancel) {
      this.#pointerEndController = listenForPointerEnd(event, () => this.finishPointerInteraction());
    }
    this.#prefetchOption(event.target);
  }

  /**
   * Restarts the canceled current render if pointer activation produced no change.
   */
  finishPointerInteraction() {
    const interaction = this.#pointerInteraction;
    this.#stopTrackingPointer();
    if (!interaction) return;

    setTimeout(() => {
      if (this.#pointerInteraction !== interaction) return;
      this.#pointerInteraction = undefined;
      if (!interaction.input.isConnected || interaction.input.checked !== interaction.checked) return;

      const facetsForm = this.closest('facets-form-component');
      if (facetsForm instanceof FacetsFormComponent) facetsForm.refreshCurrentSection();
    }, 0);
  }

  #stopTrackingPointer() {
    this.#pointerEndController?.abort();
    this.#pointerEndController = undefined;
  }

  /**
   * Handles delayed pointer hover prefetching on facet labels.
   * @param {PointerEvent} event - The pointer event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement} sliderMin - The lower range input
 * @property {HTMLInputElement} sliderMax - The upper range input
 * @property {HTMLElement} sliderRange - The highlighted part of the track
 * @property {HTMLElement} valueMin - The lower price label
 * @property {HTMLElement} valueMax - The upper price label
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  requiredRefs = ['minInput', 'maxInput', 'sliderMin', 'sliderMax', 'sliderRange', 'valueMin', 'valueMax'];

  #pointerInteraction;
  #pointerEndController;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopTrackingPointer();
  }

  /**
   * Invalidates an older response before the range control commits a change.
   */
  startPointerInteraction(event) {
    if (!event.isPrimary || event.button !== 0) return;
    const input = event.composedPath()[0];
    if (!(input instanceof HTMLInputElement) || input.type !== 'range') return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    this.#stopTrackingPointer();
    const didCancel = facetsForm.cancelPendingRender();
    this.#pointerInteraction = didCancel ? { input, value: input.value } : undefined;
    if (didCancel) {
      this.#pointerEndController = listenForPointerEnd(event, () => this.finishPointerInteraction());
    }
  }

  /**
   * Restarts the canceled current render if the range interaction produced no change.
   */
  finishPointerInteraction() {
    const interaction = this.#pointerInteraction;
    this.#stopTrackingPointer();
    if (!interaction) return;

    setTimeout(() => {
      if (this.#pointerInteraction !== interaction) return;
      this.#pointerInteraction = undefined;
      if (!interaction.input.isConnected || interaction.input.value !== interaction.value) return;

      const facetsForm = this.closest('facets-form-component');
      if (facetsForm instanceof FacetsFormComponent) facetsForm.refreshCurrentSection();
    }, 0);
  }

  #stopTrackingPointer() {
    this.#pointerEndController?.abort();
    this.#pointerEndController = undefined;
  }

  /**
   * Redraws the slider while a thumb moves.
   * @param {Event} event - The input event
   */
  handleSliderInput(event) {
    this.#normalizeSlider(event.composedPath()[0]);
  }

  /**
   * Updates price filter and results after a committed change.
   * @param {Event} event - The change event
   */
  updatePriceFilterAndResults(event) {
    this.#stopTrackingPointer();
    this.#pointerInteraction = undefined;
    // Declarative events report the component as target; the moved thumb is the origin.
    this.#normalizeSlider(event.composedPath()[0]);

    // Bounds are omitted from the URL so Shopify keeps its full price range.
    const { minInput, maxInput, sliderMin, sliderMax } = this.refs;
    minInput.value = Number(sliderMin.value) > Number(sliderMin.min) ? sliderMin.value : '';
    maxInput.value = Number(sliderMax.value) < Number(sliderMax.max) ? sliderMax.value : '';

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSummary();
  }

  /**
   * Keeps the thumbs ordered and redraws the highlighted range and labels.
   * @param {EventTarget | null} activeInput - The range input that changed
   */
  #normalizeSlider(activeInput) {
    const { sliderMin, sliderMax, sliderRange, valueMin, valueMax } = this.refs;
    const lowerBound = Number(sliderMin.min);
    const upperBound = Number(sliderMin.max);

    let min = Number(sliderMin.value);
    let max = Number(sliderMax.value);

    if (min > max) {
      if (activeInput === sliderMin) min = max;
      else max = min;
    }

    sliderMin.value = String(min);
    sliderMax.value = String(max);

    const span = upperBound - lowerBound || 1;
    sliderRange.style.left = `${((min - lowerBound) / span) * 100}%`;
    sliderRange.style.right = `${100 - ((max - lowerBound) / span) * 100}%`;

    // Both thumbs overlap at the upper bound; keep the lower one draggable.
    sliderMin.classList.toggle('range-slider__input--raised', min >= upperBound);

    const slider = sliderMin.closest('.range-slider');
    const format = slider?.getAttribute('data-label-format') || '{{amount_no_decimals}}';
    const currency = slider?.getAttribute('data-currency') || '';
    valueMin.textContent = formatMoneyTemplate(min * 100, format, currency, 0);
    valueMax.textContent = formatMoneyTemplate(max * 100, format, currency, 0);
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input:not([type="range"])').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    // Fallback for when sorting is outside facets-form-component (e.g., vertical filter style)
    if (!(facetsForm instanceof FacetsFormComponent)) {
      this.#updateSortingDirectly(event);
      return;
    }

    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Fallback method to update sorting directly when facets-form-component is not available
   * @param {Event} event - The change event
   */
  #updateSortingDirectly(event) {
    // Get the sort value from the event target
    let sortValue = '';
    if (event.target instanceof HTMLInputElement && event.target.name === 'sort_by') {
      sortValue = event.target.value;
    } else if (event.target instanceof HTMLSelectElement && event.target.name === 'sort_by') {
      sortValue = event.target.value;
    }

    if (!sortValue) return;

    // Build new URL with sort_by parameter
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    // Remove page parameter to go back to first page
    url.searchParams.delete('page');

    // Update browser history
    history.pushState({ sortBy: sortValue }, '', url.toString());

    // Get section ID and re-render
    const section = this.closest('.shopify-section');
    if (!section) return;

    const sectionId = section.id.replace('shopify-section-', '');
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(sectionId);
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    return formatMoneyTemplate(moneyValue, template, this.refs.facetStatus.dataset.currency || '');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Formats money like the `money` Liquid filters for a Shopify money format.
 * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
 * @param {string} template - The money format, e.g. `{{amount_with_comma_separator}} €`
 * @param {string} currency - The ISO currency code
 * @param {number} [precisionOverride] - Decimal places to use instead of the currency default
 * @returns {string} The formatted money value
 */
function formatMoneyTemplate(moneyValue, template, currency, precisionOverride) {
  return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';
    if (placeholder === 'currency') return currency;

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

    if (placeholder === 'amount') {
      // Check first since it's the most common, use defaults.
    } else if (placeholder === 'amount_no_decimals') {
      precision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      // Weirdly, this is correct. It uses amount_with_comma_separator's
      // behaviour but removes decimals, resulting in an unintuitive
      // output that can't possibly include commas, despite the name.
      thousandsSeparator = '.';
      precision = 0;
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousandsSeparator = ' ';
      precision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    }

    return formatCents(moneyValue, thousandsSeparator, decimalSeparator, precisionOverride ?? precision);
  });
}

/**
 * Formats money in cents
 * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
 * @param {string} thousandsSeparator - The thousands separator
 * @param {string} decimalSeparator - The decimal separator
 * @param {number} precision - The precision
 * @returns {string} The formatted money value
 */
function formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
  const roundedNumber = (moneyValue / 100).toFixed(precision);

  let [a, b] = roundedNumber.split('.');
  if (!a) a = '0';
  if (!b) b = '';

  // Split by groups of 3 digits
  a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

  return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
